import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IconPackage } from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Text } from "../../../../components/common/typography";
import { EmptyState, type EmptyStateProps } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { PaginatedResponse } from "../../../pagination/data";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { Pagination } from "../../../../components/common/pagination";
import { ProductCard } from "./product-card";
import type { ProductCardProps } from "./product-card";
import { getProductDetailsUrl } from "../../utils/get-product-details-url";
interface ProductsGridViewProps<TQuery> {
    useDataHook: (query?: TQuery) => {
        data?: PaginatedResponse<ProductDto>;
        isLoading: boolean;
        error?: Error;
    };
    baseQuery?: TQuery;
    pageSize?: number;
    emptyState?: EmptyStateProps;
    compareMode?: boolean;
    selectedProducts?: Set<number>;
    onProductToggle?: (product: ProductDto) => void;
    /** When true, disables unselected "Add to compare" buttons — used when the compare list is full. */
    compareDisabled?: boolean;
    productDetailsRoute?: string;
    cardPropsGetter?: (product: ProductDto) => Partial<ProductCardProps>;
    hidePagination?: boolean;
}
export function ProductsGridView<TQuery>({ useDataHook, baseQuery = {} as TQuery, pageSize = 12, emptyState, compareMode = false, selectedProducts, onProductToggle, compareDisabled = false, productDetailsRoute = AppRoutes.PRODUCT_DETAILS, cardPropsGetter, hidePagination = false, }: ProductsGridViewProps<TQuery>) {
    const navigate = useNavigate();
    const { pagination, setPagination, sortingQuery } = usePaginationSortingUrlSync({
        defaultPageSize: pageSize,
    });
    // Prop is authoritative — URL may carry a stale pageSize from a previous visit
    useEffect(() => {
        if (pagination.pageSize !== pageSize) {
            setPagination((p) => ({ ...p, pageSize }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);
    const hookQuery: TQuery = {
        ...baseQuery,
        ...sortingQuery,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
    } as TQuery;
    const swrResponse = useDataHook(hookQuery);
    const response = swrResponse.data;
    const isLoading = swrResponse.isLoading ?? (!response && !swrResponse.error);
    const error = swrResponse.error;
  const isEmpty = !isLoading && (!response?.data || response.data.length === 0);
  const openProductDetails = (product: ProductDto) => {
    navigate(
      getProductDetailsUrl(
        product.id,
        product.variantId ?? null,
        productDetailsRoute,
      ),
    );
  };
    const handleCardClick = (product: ProductDto) => {
        if (compareMode) {
            onProductToggle?.(product);
        }
        else {
            openProductDetails(product);
        }
    };
    const defaultEmptyState: EmptyStateProps = useMemo(() => ({
        icon: IconPackage,
        title: "No products yet",
    }), []);
    if (error) {
        return (<div className="flex items-center justify-center h-full min-h-[400px]">
        <Text variant="body" className="text-accentError">
          Error loading data: {error.message}
        </Text>
      </div>);
    }
    if (isEmpty && (emptyState || defaultEmptyState)) {
        return (<div className="flex items-center justify-center h-full min-h-[400px]">
        <EmptyState {...(emptyState || defaultEmptyState)}/>
      </div>);
    }
    return (<div className="flex flex-col h-full">
      {isLoading ? (<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: pageSize }).map((_, index) => (<div key={index} className="bg-uiSecondary rounded-lg p-4 animate-pulse">
              <div className="w-full h-48 bg-gray-300 rounded-lg mb-4"/>
              <div className="h-4 bg-gray-300 rounded mb-2"/>
              <div className="h-4 bg-gray-300 rounded w-2/3"/>
            </div>))}
        </div>) : (<>
          <div className="grid grid-cols-1 gap-6 pb-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {response?.data?.map((product) => {
                const isSelected = selectedProducts?.has(product.id) ?? false;
                return (<div key={product.id} onClick={() => handleCardClick(product)} className="h-full cursor-pointer">
                  <ProductCard product={product} selectable={compareMode} selected={isSelected} compareDisabled={compareDisabled} onToggleSelect={() => onProductToggle?.(product)} onViewDetails={() => openProductDetails(product)} {...(cardPropsGetter ? cardPropsGetter(product) : undefined)}/>
                </div>);
            })}
          </div>

          {!hidePagination && response?.meta &&
                response.meta.pageCount &&
                response.meta.pageCount > 1 && (<div className="mt-auto flex justify-center border-t border-uiSecondary pt-4">
                <Pagination page={pagination.pageIndex + 1} pageCount={response.meta.pageCount} onPageChange={(page) => setPagination({ ...pagination, pageIndex: page - 1 })}/>
              </div>)}
        </>)}
    </div>);
}
